import { InputMask } from 'imask';
import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IMaskInput } from 'react-imask';

import Icon from '~/components/Icon/Icon';
import InputText from '~/components/InputText/InputText';
import ApplySMSButton from '~/components/ModalAuth/ApplySMSButton/ApplySMSButton';
import SmsRequestTimer from '~/components/ModalAuth/SmsRequestTimer/SmsRequestTimer';

import styles from './styles.module.scss';

interface CodeVerificationFormProps {
  phoneNumber: string;
  codeVal: string;
  isCodeValid: boolean;
  phoneValid: boolean;
  isLoading: boolean;
  isDeletedUser: boolean;
  canRequestAgain: boolean;
  requestCode: (useDokobit?: boolean) => void;
  setCanRequestAgain: (canRequestAgain: boolean) => void;
  onCodeChange: (val: string, mask: InputMask<any>) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onChangePhone?: () => void;
}

export default function CodeVerificationForm({
  phoneNumber,
  codeVal,
  isCodeValid,
  phoneValid,
  isLoading,
  isDeletedUser,
  canRequestAgain,
  requestCode,
  setCanRequestAgain,
  onCodeChange,
  onSubmit,
  onChangePhone,
}: CodeVerificationFormProps) {
  const { t } = useTranslation();
  const [shouldResetTimer, setShouldResetTimer] = useState(false);

  const canRequestCode = canRequestAgain && !isCodeValid;

  useEffect(() => {
    if (!canRequestAgain && shouldResetTimer) {
      setShouldResetTimer(false);
    }
  }, [canRequestAgain, shouldResetTimer]);

  const handleTimerEnd = () => {
    setCanRequestAgain(true);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (canRequestCode) {
      setShouldResetTimer(true);
      requestCode(false);
    } else {
      onSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.content}>
        <div className={styles.info}>
          <h1 className={styles.title}>{t('phrases:verifyPhoneNumber')}</h1>
          <span className={styles.description}>
            {t('phrases:enterCode', { count: 4 })}
          </span>
          <div className={styles.phone}>{phoneNumber}</div>
        </div>

        <InputText
          attributeName="auth-code"
          className="modal-auth__input-code"
          label=""
          value={codeVal}
        >
          <IMaskInput
            data-input="auth-code-mask"
            className="input-text"
            mask="0000"
            value={codeVal}
            unmask={true}
            type="tel"
            autoFocus={true}
            onAccept={onCodeChange}
            autoComplete="one-time-code"
          />
        </InputText>

        <div className={styles.actions}>
          <button
            type="button"
            data-button="auth-change-phone"
            className="button _bordered _block _med"
            onClick={onChangePhone}
          >
            <Icon type="arrow" className="_mirror-x" />
            {t('changeNumber')}
          </button>

          {!isCodeValid ? (
            <button
              data-button="request-code"
              className="button _primary _med"
              disabled={!canRequestCode}
              type="submit"
            >
              <SmsRequestTimer
                onTimerEnd={handleTimerEnd}
                resetTimer={shouldResetTimer}
              />
            </button>
          ) : (
            <ApplySMSButton
              isLoading={isLoading}
              isDisabled={!phoneValid || !isCodeValid}
            >
              {isDeletedUser ? t('enterOTPAndRecoverAccount') : t('enterOTP')}
            </ApplySMSButton>
          )}
        </div>
      </div>
    </form>
  );
}
